'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a risk weight (higher = more risk) and a gap flag that
// surfaces a specific recommendation when chosen.
interface Option {
  text: string;
  risk: number; // 0 (good) .. 3 (worst)
  gap?: string; // recommendation triggered when this option is selected
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'enrollment',
    text: 'Are phones and tablets that access work data enrolled in an MDM?',
    helpText: 'MDM is how you control devices holding company data.',
    options: [
      { text: 'Yes—all devices are enrolled and managed', risk: 0 },
      { text: 'Only company-owned devices, not personal ones', risk: 2, gap: 'Personal (BYOD) devices accessing email and files are unmanaged. Enroll them in MDM with a work container.' },
      { text: 'No MDM / not sure', risk: 3, gap: 'Without MDM you have no control over devices holding company data. Deploying MDM is the foundational fix.' },
    ],
  },
  {
    id: 'byod',
    text: 'Is there a clear BYOD policy separating work and personal data?',
    helpText: 'Containerizing work data keeps it manageable and wipeable.',
    options: [
      { text: 'Yes—work data is containerized and controlled', risk: 0 },
      { text: 'Personal devices are used but with no separation', risk: 2, gap: 'Mixing work and personal data risks leakage and complicates wiping. Containerize work data so it can be managed and wiped independently.' },
      { text: 'No BYOD policy / not sure', risk: 2, gap: 'An undefined BYOD posture means uncontrolled access. Define and enforce a BYOD policy with work/personal separation.' },
    ],
  },
  {
    id: 'lock_encryption',
    text: 'Are screen locks and device encryption enforced?',
    helpText: 'Unencrypted, unlocked devices expose all data if lost.',
    options: [
      { text: 'Yes—passcode and encryption enforced via policy', risk: 0 },
      { text: 'Recommended but not enforced', risk: 2, gap: 'Optional passcodes/encryption mean many devices have neither. Enforce them through MDM policy.' },
      { text: 'No / not sure', risk: 3, gap: 'Unencrypted, unlocked devices expose all data if lost. Enforce passcode and encryption as a baseline.' },
    ],
  },
  {
    id: 'remote_wipe',
    text: 'Can you remotely lock or wipe a lost or stolen device?',
    helpText: 'A lost phone without remote wipe is an open breach.',
    options: [
      { text: 'Yes—remote lock/wipe is in place and tested', risk: 0 },
      { text: 'Maybe for some devices, never tested', risk: 1, gap: 'Untested wipe fails when you need it. Confirm remote lock/wipe works across all enrolled devices.' },
      { text: 'No remote wipe / not sure', risk: 3, gap: 'A lost phone without remote wipe is an open data breach. Enable remote lock/selective wipe for every device.' },
    ],
  },
  {
    id: 'patching',
    text: 'Are mobile OS and app updates enforced?',
    helpText: 'Outdated mobile OSes carry known exploits.',
    options: [
      { text: 'Yes—OS/app compliance is enforced', risk: 0 },
      { text: 'Left to each user', risk: 2, gap: 'User-managed updates leave devices on vulnerable OS versions. Enforce minimum OS/patch compliance via MDM.' },
      { text: 'No update enforcement / not sure', risk: 2, gap: 'Enforce update compliance and block non-compliant devices.' },
    ],
  },
  {
    id: 'threat_defense',
    text: 'Is there mobile threat defense against malicious apps and phishing?',
    helpText: 'Phones are increasingly phishing targets.',
    options: [
      { text: 'Yes—MTD blocks malicious apps and mobile phishing', risk: 0 },
      { text: 'Relying on app-store vetting only', risk: 1, gap: 'App-store vetting misses sideloaded apps and SMS/phishing attacks. Add mobile threat defense.' },
      { text: 'No mobile threat protection / not sure', risk: 2, gap: 'Add mobile threat defense to catch malicious apps and links.' },
    ],
  },
];

type Status = 'intro' | 'assessment' | 'results';

interface Answer {
  questionId: string;
  optionIndex: number;
}

function levelFor(percent: number): {
  level: 'High' | 'Medium' | 'Low';
  color: string;
  bg: string;
  blurb: string;
} {
  if (percent >= 55) {
    return {
      level: 'High',
      color: 'text-red-700 dark:text-red-300',
      bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      blurb:
        'Your mobile fleet has serious gaps—the kind that turn a single lost phone into a data breach. Enrolling every device in MDM and enforcing encryption and remote wipe should be an immediate priority.',
    };
  }
  if (percent >= 25) {
    return {
      level: 'Medium',
      color: 'text-amber-700 dark:text-amber-300',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      blurb:
        'You have some mobile controls in place but meaningful gaps remain. Closing them—especially BYOD separation, enforced encryption, and remote wipe—will materially lower your exposure.',
    };
  }
  return {
    level: 'Low',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    blurb:
      'Your mobile posture is strong. A managed service keeps it that way—enrolling new devices, enforcing policy, and wiping lost ones as you grow.',
  };
}

export default function RiskCheckClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [userCount, setUserCount] = useState<number>(25);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length !== QUESTIONS.length) return null;
    let total = 0;
    const maxPerQuestion = 3;
    const gaps: string[] = [];
    QUESTIONS.forEach((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      if (!a) return;
      const option = q.options[a.optionIndex];
      total += option.risk;
      if (option.gap) gaps.push(option.gap);
    });
    const max = QUESTIONS.length * maxPerQuestion;
    const percent = Math.round((total / max) * 100);
    return { percent, gaps, ...levelFor(percent) };
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = QUESTIONS.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      const chosen = a ? q.options[a.optionIndex].text : 'N/A';
      return `  • ${q.text} → ${chosen}`;
    }).join('\n');

    const gapLines = result.gaps.length
      ? result.gaps.map((g, i) => `${i + 1}. ${g}`).join('\n')
      : 'No major gaps identified—maintain current controls.';

    return `MOBILE DEVICE RISK CHECK RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Mobile Risk: ${result.level} (${result.percent}% risk score)
Approx. phones / tablets: ${userCount}

${result.blurb}

ANSWERS:
${answerLines}

TOP GAPS TO CLOSE:
${gapLines}`;
  }, [result, answers, userCount]);

  // Intro
  if (status === 'intro') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">📱</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">How exposed are your mobile devices?</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
              Six questions on MDM enrollment, BYOD separation, encryption, remote wipe, OS patching, and mobile threat defense. Get an instant Mobile Risk score and your top gaps.
            </p>
          </div>

          <div className="bg-[#2813e8]/5 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll learn:</h3>
            <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
              <li>• Whether your mobile risk is High, Medium, or Low</li>
              <li>• The specific gaps leaving you exposed</li>
              <li>• What to fix first to close the biggest holes</li>
            </ul>
          </div>

          <button
            onClick={() => setStatus('assessment')}
            className="w-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
          >
            Start the Risk Check
          </button>
        </div>
      </div>
    );
  }

  // Assessment
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Mobile Device Risk Check</span>
              <span>Question {currentIndex + 1} of {QUESTIONS.length}</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{question.text}</h2>
            {question.helpText && <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>}
          </div>

          <div className="space-y-3 mb-6">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  currentAnswer?.optionIndex === idx
                    ? 'border-[#2813e8] bg-[#2813e8]/5'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/40 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (status === 'results' && result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Score */}
        <div className={`rounded-2xl border-2 p-8 ${result.bg}`}>
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{result.level === 'High' ? '🚨' : result.level === 'Medium' ? '⚠️' : '✅'}</div>
            <h2 className={`text-3xl font-bold mb-2 ${result.color}`}>Mobile Risk: {result.level}</h2>
            <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto">{result.blurb}</p>
          </div>
          <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk score</div>
            <div className={`text-3xl font-bold ${result.color}`}>{result.percent}%</div>
          </div>
        </div>

        {/* Gaps */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {result.gaps.length ? 'Top Gaps to Close' : 'No Major Gaps Identified'}
          </h3>
          {result.gaps.length ? (
            <ul className="space-y-3">
              {result.gaps.map((gap, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2813e8]/10 text-xs font-bold text-[#2813e8]">{i + 1}</span>
                  <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{gap}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Your controls look solid. A managed service keeps enrollment, encryption, and remote wipe tight as you scale.</p>
          )}
        </div>

        {/* Device count */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            How many phones and tablets need securing? <span className="text-[#2813e8] font-bold">{userCount}</span>
          </label>
          <input
            type="range"
            min="1"
            max="500"
            step="1"
            value={userCount}
            onChange={(e) => setUserCount(Number(e.target.value))}
            aria-label="Number of phones and tablets"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>1</span>
            <span>250</span>
            <span>500</span>
          </div>
        </div>

        {/* Offer CTA */}
        <div className="rounded-2xl border-2 border-[#2813e8]/30 bg-[#2813e8]/5 p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Want these gaps closed for you?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Inventive HQ enrolls and secures every phone and tablet—enforcing encryption, separating work from personal, and wiping lost devices—as a flat per-device managed service.
          </p>
          <Link
            href="/services/mobile-device-management"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105"
          >
            See Managed MDM
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            🔄 Retake Check
          </button>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureForm
            source="mdm"
            title="Get Your Mobile Device Risk Reviewed by an Expert"
            description="Our team will review your results and send a prioritized MDM and mobile-security rollout plan."
            deliverable="a prioritized MDM/mobile-security rollout plan"
            resultsSummary={resultsSummary}
          />
        </div>
      </div>
    );
  }

  return null;
}
